// De jure owner per hex at a date, under a point of view (ARCHITECTURE §3.5).
//
//   today (human.grid.bin owner)
//      │  undo every BorderChange, newest first (hex → from)
//      ▼
//   base 2000 ──apply changes ≤ D (hex → to)──▶ default owner at D ──POV entries active at D──▶ owner
//
// Arrays returned here are SHARED and READ-ONLY: every snapshot with the same border epoch and
// the same active POV claims gets the same Uint16Array. Callers copy before writing.

import type { BorderChange, DateStr, PovOverrides, SourceId } from '../core/schema.ts';
import { Lru } from './cache.ts';

export type PovKey = keyof PovOverrides;

/** The Natural Earth edition every owner array starts from (docs/bake/borders.md). */
const BASE_BORDER_SOURCE: SourceId = 'brd:ne-admin0';

/**
 * NE admin-1 units whose owner the borders bake overrides with the UN General Assembly majority
 * position (docs/bake/borders.md, "De jure rule"), and the sources behind each override. Golan
 * Heights and East Jerusalem are absent on purpose: their correction changes no hex, and the
 * grid keeps no per-hex record of a disputed area.
 */
const CRIMEA_SOURCES: SourceId[] = ['brd:wiki-unga-68-262', 'brd:ne-admin1'];
const W_SAHARA_SOURCES: SourceId[] = ['brd:wiki-western-sahara-status', 'brd:wiki-nsgt', 'brd:ne-disputed'];
const OVERRIDE_SOURCES: Record<string, SourceId[]> = {
  'RUS-283': CRIMEA_SOURCES,
  'RUS-5482': CRIMEA_SOURCES,
  'MAR-3456': W_SAHARA_SOURCES,
  'MAR-3469': W_SAHARA_SOURCES,
  'SOL+00?': ['brd:wiki-somaliland-recognition'],
  'CYN+00?': ['brd:wiki-unsc-541', 'brd:wiki-northern-cyprus'],
};

/** Distinct (epoch, POV claim set) arrays kept; real data has ~7 epochs × a few claim sets. */
const MAX_POV_ARRAYS = 24;

export interface OwnerModel {
  base: Uint16Array;
  /** Oldest first. */
  changes: BorderChange[];
  pov: PovOverrides;
  /** Default-worldview owner after the first k changes, built on demand. */
  steps: Array<Uint16Array | undefined>;
  povCache: Lru<string, Uint16Array>;
  /** Claim arrays by the (shared) POV owner array they were derived from. */
  claims: WeakMap<Uint16Array, Uint16Array>;
  /** All zeros: the claim array of a view that agrees with the default worldview. */
  noClaims: Uint16Array;
}

function applyChange(owner: Uint16Array, c: BorderChange): void {
  for (const [hex, , to] of c.hexes) {
    owner[hex] = to;
  }
}

export function ownerModel(today: Uint16Array, changes: BorderChange[], pov: PovOverrides): OwnerModel {
  const sorted = changes.slice().sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

  // Undo changes newest first, so a hex changed twice ends at its 2000 owner.
  const base = today.slice();
  for (let i = sorted.length - 1; i >= 0; i--) {
    for (const [hex, from] of sorted[i].hexes) {
      base[hex] = from;
    }
  }
  return {
    base, changes: sorted, pov, steps: [base], povCache: new Lru(MAX_POV_ARRAYS),
    claims: new WeakMap(), noClaims: new Uint16Array(today.length),
  };
}

/** Number of border changes in force at `date`. */
export function epochAt(m: OwnerModel, date: DateStr): number {
  let k = 0;
  while (k < m.changes.length && m.changes[k].date <= date) {
    k++;
  }
  return k;
}

function stepArray(m: OwnerModel, k: number): Uint16Array {
  const hit = m.steps[k];
  if (hit) {
    return hit;
  }
  const arr = stepArray(m, k - 1).slice();
  applyChange(arr, m.changes[k - 1]);
  m.steps[k] = arr;
  return arr;
}

/** Default-worldview owner at `date` (shared, read-only). */
export function defaultOwners(m: OwnerModel, date: DateStr): Uint16Array {
  return stepArray(m, epochAt(m, date));
}

/** Indices of the POV entries whose claim is in force at `date`. */
export function activeClaims(m: OwnerModel, pov: PovKey, date: DateStr): number[] {
  const out: number[] = [];
  m.pov[pov].forEach((e, i) => {
    if (e.since <= date && (e.to === null || date < e.to)) {
      out.push(i);
    }
  });
  return out;
}

/** Owner at `date` under `pov` (shared, read-only). */
export function ownersAt(m: OwnerModel, date: DateStr, pov: PovKey): Uint16Array {
  const k = epochAt(m, date);
  const claims = activeClaims(m, pov, date);
  if (claims.length === 0) {
    return stepArray(m, k);
  }

  const key = `${k}|${pov}|${claims.join(',')}`;
  return m.povCache.use(key, () => {
    const arr = stepArray(m, k).slice();
    for (const i of claims) {
      for (const [hex, id] of m.pov[pov][i].hexes) {
        arr[hex] = id;
      }
    }
    return arr;
  });
}

/**
 * Where the POV owner differs from the default owner, the default owner's actor index; else 0.
 *
 *   default   .  UKR UKR RUS
 *   view      .  RUS RUS RUS      →  claim  0  UKR UKR  0
 *
 * `owner` and `defOwner` come from ownersAt / defaultOwners at one date. An owner array is shared
 * per (epoch, claim set), so the result is memoised on it (shared, read-only).
 */
export function claimsOf(m: OwnerModel, owner: Uint16Array, defOwner: Uint16Array): Uint16Array {
  if (owner === defOwner) {
    return m.noClaims;
  }

  const hit = m.claims.get(owner);
  if (hit) {
    return hit;
  }
  const out = new Uint16Array(owner.length);
  for (let h = 0; h < owner.length; h++) {
    out[h] = owner[h] === defOwner[h] ? 0 : defOwner[h];
  }
  m.claims.set(owner, out);
  return out;
}

/**
 * Why `hex` has its owner at `date` under `pov`, as sources:
 *
 *   base edition ── latest BorderChange ≤ date that moved the hex ── POV claim in force on it
 *
 * Only the layers that apply to this hex are listed. `unit` is the hex's NE admin-1 code: a unit
 * the bake re-assigned adds the sources of that override to the base edition's.
 */
export function ownerSources(m: OwnerModel, date: DateStr, pov: PovKey | null, hex: number, unit: string | null): SourceId[] {
  const out: SourceId[] = [BASE_BORDER_SOURCE, ...((unit && OVERRIDE_SOURCES[unit]) || [])];

  // Newest change first: the last one that touched the hex decides its de jure owner.
  for (let i = epochAt(m, date) - 1; i >= 0; i--) {
    if (m.changes[i].hexes.some((h) => h[0] === hex)) {
      out.push(...m.changes[i].sources);
      break;
    }
  }

  // A state view (sandbox) has no point of view, so no claim adds sources.
  if (pov === null) {
    return [...new Set(out)];
  }

  for (const i of activeClaims(m, pov, date)) {
    const claim = m.pov[pov][i];
    if (claim.hexes.some((h) => h[0] === hex)) {
      out.push(...claim.sources);
    }
  }
  return [...new Set(out)];
}
